import { StateService } from "@uirouter/angular";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { LineChart } from "echarts/charts";
import { DataZoomComponent, DatasetComponent, GraphicComponent, GridComponent, MarkPointComponent, TooltipComponent } from "echarts/components";
import * as echarts from "echarts/core";
import { SVGRenderer } from "echarts/renderers";
import { take, zip } from "rxjs";
import { localize } from "@gtmhub/localization";
import { Comment } from "@webapp/comments";
import {
  IKpiChartPoint,
  KpiChartCommentData,
  KpiChartCommentMarkPointData,
  KpiChartData,
  KpiChartPointMenuItem,
  KpiChartPopoverOptions,
} from "@webapp/kpis/components/kpi-details/kpi-chart/kpi-chart.models";
import { KpiChartOptionsBuilder } from "@webapp/kpis/components/kpi-details/kpi-chart/services/kpi-chart-options-builder";
import { KpiDetailsStateService } from "@webapp/kpis/components/kpi-details/services/kpi-details-state.service";
import { KpiSnapshotModalService } from "@webapp/kpis/components/kpi-snapshot-form/kpi-snapshot-modal.service";
import { KpiProjection, KpiProjectionOpenedFrom } from "@webapp/kpis/models/kpi-projections.models";
import { KpiSnapshot } from "@webapp/kpis/models/kpi-snapshot.models";
import dayjs from "@webapp/shared/libs/dayjs";
import { PositionStyle } from "@webapp/shared/models";
import { UiPopoverDirective } from "@webapp/ui/popover/popover.directive";
import { KpiDetailsStateProxyService } from "../services/kpi-details-state-proxy.service";
import { KpiChartCommentsBuilder } from "./services/kpi-chart-comments.builder";
import { KpiChartUtilsService } from "./services/kpi-chart-utils.service";
import { CHART_POINT_DATE_FORMAT, formatKpiValue } from "./utils";

echarts.use([TooltipComponent, GridComponent, DatasetComponent, LineChart, SVGRenderer, DataZoomComponent, GraphicComponent, MarkPointComponent]);

@UntilDestroy()
@Component({
  selector: "kpi-chart",
  templateUrl: "./kpi-chart.component.html",
  styleUrls: ["kpi-chart.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [KpiChartOptionsBuilder, KpiChartUtilsService, KpiChartCommentsBuilder],
})
export class KpiChartComponent implements OnInit, OnDestroy {
  @Output() public readonly commentAdd = new EventEmitter<KpiChartCommentData>();

  @ViewChild("chartPopover", { static: false })
  private chartPopover: UiPopoverDirective;
  @ViewChild("projectionFormTemplate", { static: false })
  private projectionFormTemplate: TemplateRef<void>;
  @ViewChild("pointMenuTemplate", { static: false })
  private pointMenuTemplate: TemplateRef<void>;

  public chartData: KpiChartData;
  public projectionFormData: KpiProjection;
  public projectionFormOpenedFrom: KpiProjectionOpenedFrom;
  public popoverOriginStyle: PositionStyle = { top: "0px", left: "0px" };
  public popoverOptions: KpiChartPopoverOptions;
  public chartPointMenuItems: KpiChartPointMenuItem[];
  private lastClickedPointCommentData: KpiChartCommentData;
  private chart: echarts.ECharts;

  constructor(
    private kpiDetailsStateService: KpiDetailsStateService,
    private kpiChartOptionsBuilder: KpiChartOptionsBuilder,
    private kpiChartUtilsService: KpiChartUtilsService,
    private cdr: ChangeDetectorRef,
    private kpiSnapshotModalService: KpiSnapshotModalService,
    private stateService: StateService,
    private ngZone: NgZone,
    private kpiDetailsStateProxyService: KpiDetailsStateProxyService,
    private elementRef: ElementRef<HTMLElement>
  ) {}

  public ngOnInit(): void {
    this.setUpProjectionFormPopover();
    this.ngZone.runOutsideAngular(() => {
      this.chart = echarts.init(this.elementRef.nativeElement.querySelector<HTMLDivElement>("#kpi-echart"), null, { renderer: "svg" });
    });
    this.kpiChartOptionsBuilder
      .getDndGraphicPointClicked$()
      .pipe(untilDestroyed(this))
      .subscribe(({ point, event }) => {
        this.onDndProjectionPointClick(point, event);
      });

    this.kpiDetailsStateService
      .getChartData$()
      .pipe(untilDestroyed(this))
      .subscribe((chartData) => {
        this.chartData = chartData;
        this.buildChart();
        // detect changes is needed for empty state, and going back and forth to it
        this.cdr.detectChanges();
        this.setChartListeners();
      });
  }

  public ngOnDestroy(): void {
    this.removeChartListeners();
    this.kpiChartOptionsBuilder.unsubscribe();
    // Call echartsInstance.dispose to release resources while disposing the node to avoid memory leaks.
    // https://echarts.apache.org/handbook/en/concepts/chart-size/#dispose-and-rebuild-of-the-container-node
    this.chart?.dispose();
  }

  public onClickSetProjectionButton(): void {
    // the chart maybe zoomed/scrolled and we want the popover arrow to point at the new projection's potential placement on the chart
    this.kpiDetailsStateService.refreshChartData();
    const { projections, snapshots } = this.chartData;
    const value = this.generateNewProjectionValue(snapshots);
    const rawDate = this.generateNewProjectionDate(projections);
    const date = dayjs(rawDate).format(CHART_POINT_DATE_FORMAT);
    const [x, y] = this.chart.convertToPixel({ seriesId: "projectionSeries" }, [rawDate.getTime(), value]);
    this.popoverOriginStyle = { left: `${x}px`, top: `${y}px` };
    this.setUpProjectionFormData({ date, value: null, id: null }, "button");
    this.openProjectionForm();
  }

  public openSnapshotFormModal(): void {
    zip(this.kpiDetailsStateService.getKpi$(), this.kpiDetailsStateService.getSnapshots$())
      .pipe(take(1))
      .subscribe(([kpi, snapshots]) => {
        this.kpiSnapshotModalService
          .openKpiSnapshotModal(kpi, snapshots)
          .afterClose.pipe(take(1))
          .subscribe((isSnapshotUpdated) => {
            if (isSnapshotUpdated) {
              this.kpiDetailsStateService.fetchKpiSnapshots(kpi.id);
            }
          });
      });
  }

  public onAutomateClick(): void {
    this.stateService.go("._singleKpi.form", { kpiId: this.chartData.kpi.id });
  }

  @HostListener("window:resize")
  public resizeChart(): void {
    this.chart.resize();
  }

  private buildChart(): void {
    const { snapshots, kpi, projections, timeFilter, canEditKpi } = this.chartData;
    this.kpiDetailsStateService
      .getShouldBuildChart$()
      .pipe(take(1))
      .subscribe((shouldBuildChart) => {
        if (shouldBuildChart) {
          // The order should be exactly this
          this.kpiChartOptionsBuilder
            .forKpi(kpi)
            .inChartInstance(this.chart)
            .withSnapshots(snapshots)
            .withProjections(projections)
            .withTimeFilter(timeFilter)
            .build({ canEditKpi });
        }
        this.kpiDetailsStateService.setShouldBuildChart(true);
      });
  }

  private openProjectionForm(): void {
    this.setUpProjectionFormPopover();
    this.cdr.detectChanges();
    this.ngZone.run(() => {
      this.chartPopover.show();
    });
  }

  private togglePointMenu(): void {
    this.setUpChartPointMenuPopover();
    this.cdr.detectChanges();
    this.chartPopover.show();
  }

  private onDndProjectionPointClick(point: IKpiChartPoint, event: echarts.ElementEvent): void {
    const { date, projectionId, projection } = point;
    this.popoverOriginStyle = { left: `${event.event.zrX}px`, top: `${event.event.zrY}px` };
    this.setUpProjectionFormData({ id: projectionId, date, value: formatKpiValue(projection, this.chartData.kpi.formatting.fractionSize) }, "chart");

    this.setUpCommentData({ targetType: "kpi_projection", targetDate: date, targetValue: projection });
    this.setUpProjectionMenuItems();
    this.togglePointMenu();
  }

  private handleOnChartPointClick(): void {
    this.chart.on("click", this.onChartPointClick);
  }

  private handleOnChartGridClick(): void {
    this.chart.getZr().on("click", this.onChartGridClick);
  }

  private handleOnChartMousemove(): void {
    this.chart.getZr().on("mousemove", this.styleCursor);
  }

  private setChartListeners(): void {
    this.removeChartListeners();
    if (this.chartData.canEditKpi) {
      this.handleOnChartGridClick();
    }

    this.handleOnChartPointClick();

    this.handleOnChartMousemove();
  }

  private generateNewProjectionDate(projections: KpiProjection[]): Date {
    if (!projections.length || this.allProjectionsAreInThePast(projections)) {
      return dayjs().endOf("month").add(1, "days").toDate();
    } else {
      const lastProjectionDate = projections[projections.length - 1].date;
      return dayjs(lastProjectionDate).endOf("month").add(15, "days").toDate();
    }
  }

  private allProjectionsAreInThePast(projections: KpiProjection[]): boolean {
    return projections.every((projection) => dayjs().isAfter(dayjs(projection.date)));
  }

  private generateNewProjectionValue(snapshots: KpiSnapshot[]): number {
    return snapshots.length ? snapshots[snapshots.length - 1].value : 1;
  }

  private blankSpaceOutsideChartIsClicked(event: echarts.ElementEvent): boolean {
    return !event.topTarget;
  }

  private chartAxisLabelIsClicked(event: echarts.ElementEvent): boolean {
    return event.topTarget.type === "tspan";
  }

  private chartBorderIsClicked(event: echarts.ElementEvent): boolean {
    return event.topTarget.type === "line";
  }

  private chartSeriesPointOrSnapshotCommentDropletIsClicked(event: echarts.ElementEvent): boolean {
    const { target, topTarget } = event;
    return topTarget?.type === "Line" && target?.type === "path";
  }

  private chartMarkPointIsClicked(event: echarts.ElementEvent): boolean {
    const { target, topTarget } = event;
    return topTarget?.type === "Line" && target?.type === "tspan";
  }

  private chartDragAndDropPointIsClicked(event: echarts.ElementEvent): boolean {
    const { target } = event;
    return target?.type === "circle";
  }

  private removeChartListeners(): void {
    this.chart?.getZr()?.off("mousemove", this.styleCursor);
    this.chart?.getZr()?.off("click", this.onChartGridClick);
    this.chart?.off("click", this.onChartPointClick);
  }

  // arrow function is used to keep the method reference and 'this' context when used to add/remove listener
  private onChartPointClick = (event: echarts.ECElementEvent): void => {
    const commentPinIsClicked = event.componentType === "markPoint";
    if (commentPinIsClicked) {
      this.handleCommentPinClicked(event);
      return;
    }

    // from this point on, the user should be able to Manage KPIs in order to interact with the chart
    if (!this.chartData.canManageKpis) {
      return;
    }

    // if clicking on a projection when the DnD is enabled(user can edit the KPI) the componentType is "graphic"
    if (event.componentType !== "series") {
      return;
    }

    const snapshotIsClicked = event.seriesId === "snapshotSeries";
    const projectionIsClicked = event.seriesId === "projectionSeries";
    const projectionClicksAreHandledByDndHandler = this.chartData.canEditKpi;
    if (!snapshotIsClicked && (!projectionIsClicked || projectionClicksAreHandledByDndHandler)) {
      return;
    }

    const pointData = <IKpiChartPoint>event.data;
    const targetType = snapshotIsClicked ? "kpi_snapshot" : "kpi_projection";
    const { date } = pointData;

    this.kpiDetailsStateService
      .getComments$()
      .pipe(take(1))
      .subscribe((comments) => {
        if (this.pointMenuWillBeEmpty({ date, targetType }, comments)) {
          return;
        }

        this.popoverOriginStyle = { left: `${event.event.offsetX}px`, top: `${event.event.offsetY}px` };

        if (snapshotIsClicked) {
          const { snapshot } = pointData;
          this.setUpProjectionFormData({ id: null, date, value: snapshot }, "chart");
          this.setUpCommentData({ targetType, targetDate: date, targetValue: snapshot });
          this.setUpSnapshotMenuItems();
        }

        if (projectionIsClicked) {
          const { projection, projectionId } = pointData;
          this.setUpProjectionFormData({ id: projectionId, date, value: projection }, "chart");
          this.setUpCommentData({ targetType, targetDate: date, targetValue: projection });
          this.setUpProjectionMenuItems();
        }

        this.togglePointMenu();
      });
  };

  private handleCommentPinClicked(event: echarts.ECElementEvent): void {
    const commentId = (event.data as KpiChartCommentMarkPointData).name;
    if (commentId) {
      const nextState = this.commentStateNameOnPinClick;
      this.ngZone.run(() => {
        this.kpiDetailsStateService.hideAddFirstCommentView();
        this.stateService.go(nextState, { commentId: commentId });
      });
    }
  }
  private get commentStateNameOnPinClick(): string {
    if (this.stateService.includes("**.comments")) {
      return "^.comment";
    } else {
      return this.stateService.includes("**.comment") ? "." : ".comment";
    }
  }

  // arrow function is used to keep the method reference and 'this' context when used to add/remove listener
  private onChartGridClick = (event: echarts.ElementEvent): void => {
    if (
      this.blankSpaceOutsideChartIsClicked(event) ||
      this.chartAxisLabelIsClicked(event) ||
      this.chartBorderIsClicked(event) ||
      this.chartSeriesPointOrSnapshotCommentDropletIsClicked(event) ||
      this.chartDragAndDropPointIsClicked(event) ||
      this.chartMarkPointIsClicked(event)
    ) {
      return;
    }

    this.popoverOriginStyle = { left: `${event.offsetX}px`, top: `${event.offsetY}px` };
    const [dateInMs, value] = this.chart.convertFromPixel({ seriesId: "snapshotSeries" }, [event.offsetX, event.offsetY]);
    const date = dayjs(dateInMs).format(CHART_POINT_DATE_FORMAT);
    this.setUpProjectionFormData({ id: null, date, value: formatKpiValue(value, this.chartData.kpi.formatting.fractionSize) }, "chart");
    this.openProjectionForm();
  };

  // arrow function is used to keep the method reference and 'this' context when used to add/remove listener
  private styleCursor = (event: echarts.ElementEvent): void => {
    if (event.topTarget && event.topTarget.type !== "tspan") {
      this.chart.getZr().setCursorStyle("pointer");
    }
  };

  private setUpProjectionFormData(projectionData: KpiProjection, projectionFormOpenedFrom: KpiProjectionOpenedFrom): void {
    this.projectionFormData = projectionData;
    this.projectionFormOpenedFrom = projectionFormOpenedFrom;
  }

  private setUpProjectionFormPopover(): void {
    this.popoverOptions = {
      placement: "bottom",
      content: this.projectionFormTemplate,
    };
  }

  private setUpChartPointMenuPopover(): void {
    this.popoverOptions = {
      overlayClassName: "without-arrow",
      placement: ["leftTop", "leftBottom", "rightTop", "rightBottom"],
      content: this.pointMenuTemplate,
    };
  }

  private setUpCommentData(commentData: KpiChartCommentData): void {
    this.lastClickedPointCommentData = commentData;
  }

  private setUpProjectionMenuItems(): void {
    this.chartPointMenuItems = [
      ...this.chartPointMenuDefaultItems,
      {
        label: localize("edit_projection"),
        handler: (): void => {
          this.chartPopover.hide();
          this.openProjectionForm();
        },
        isVisible$: this.kpiDetailsStateService.canEditKpi$(),
        dataTestId: "kpi-chart-point-menu-edit-projection",
      },
      {
        label: localize("delete_projection"),
        handler: (): void => {
          this.kpiChartUtilsService.deleteProjection$(this.chartData.kpi.id, this.projectionFormData.id, this.chartPopover.hide).subscribe(() => {
            this.chartPopover.hide();
            this.kpiDetailsStateProxyService.onProjectionDeleted(this.projectionFormData.date);
          });
        },
        isDestructive: true,
        isVisible$: this.kpiDetailsStateService.canEditKpi$(),
        dataTestId: "kpi-chart-point-menu-delete-projection",
      },
    ];
  }

  private setUpSnapshotMenuItems(): void {
    this.chartPointMenuItems = [
      ...this.chartPointMenuDefaultItems,
      {
        label: localize("set_projection"),
        handler: (): void => {
          this.chartPopover.hide();
          this.openProjectionForm();
        },
        isVisible$: this.kpiDetailsStateService.canEditKpi$(),
        dataTestId: "kpi-chart-point-menu-set-projection",
      },
    ];
  }

  private get chartPointMenuDefaultItems(): KpiChartPointMenuItem[] {
    return [
      {
        label: localize("add_comment"),
        handler: (): void => {
          this.chartPopover.hide();
          this.ngZone.run(() => {
            this.commentAdd.emit(this.lastClickedPointCommentData);
          });
        },
        isVisible$: this.kpiDetailsStateService.canAddCommentToPoint$(this.lastClickedPointCommentData),
        dataTestId: "kpi-chart-point-menu-add-comment",
      },
    ];
  }

  private pointMenuWillBeEmpty(pointData: { targetType: string; date: string }, comments: Comment[]): boolean {
    const pointHasComment = comments.some(
      (comment) => comment.targetType === pointData.targetType && dayjs(comment.targetDate).format(CHART_POINT_DATE_FORMAT) === pointData.date
    );

    return !this.chartData.canEditKpi && pointHasComment;
  }
}
